/**
 * StratIQX Main Navigation Component
 * Unified navigation system with desktop and mobile support
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../ui/Button'
import { UserBadge } from '../auth/UserBadge'
import { useAuthStore } from '../../stores/authStore'
import { NavigationConfig, NAVIGATION_ACTIONS } from '../../config/navigation.config'
import { Menu, X } from 'lucide-react'
import { cn } from '../../lib/utils'

interface MainNavigationProps {
  className?: string
  variant?: 'landing' | 'minimal' | 'full'
}

export const MainNavigation: React.FC<MainNavigationProps> = ({ 
  className = '', 
  variant = 'full' 
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  const navigationItems = NavigationConfig.getMainNavigation()

  const handleNavigate = (path: string) => {
    navigate(path)
    setIsMobileMenuOpen(false)
  }

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
    setIsMobileMenuOpen(false)
  }

  const isActivePage = (path: string) => {
    return location.pathname === path
  }

  if (variant === 'minimal') {
    return (
      <header className={cn('relative border-b border-blue-800/30 bg-slate-900 backdrop-blur-lg', className)}>
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={() => handleNavigate('/')}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <div className="text-2xl font-bold text-white">
                Strat<span className="text-blue-400">IQ</span><span className="text-cyan-400">X</span>
              </div>
              <div className="ml-3 text-sm text-gray-300 font-medium">
                Strategic Intelligence Amplified
              </div>
            </button>

            {/* User Badge */}
            {isAuthenticated && <UserBadge />}
          </div>
        </div>
      </header>
    )
  }

  return (
    <>
      <header className={cn('relative border-b border-blue-800/30 bg-slate-900/50 backdrop-blur-lg', className)}>
        <div className="container mx-auto px-4 py-6">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={() => handleNavigate('/')}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <div className="text-2xl font-bold text-white">
                Strat<span className="text-blue-400">IQ</span><span className="text-cyan-400">X</span>
              </div>
              <div className="ml-3 text-sm text-gray-300 font-medium">
                Strategic Intelligence Amplified
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.path)}
                  className={cn(
                    "text-gray-300 hover:text-blue-400 transition-colors font-medium",
                    {
                      'text-blue-400': isActivePage(item.path)
                    }
                  )}
                >
                  {item.label}
                </button>
              ))}
              
              {/* CTA Button */}
              {!isAuthenticated && (
                <Button 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-2 text-sm"
                >
                  {NAVIGATION_ACTIONS.primary.label}
                </Button>
              )}

              {/* User Badge for authenticated users */}
              {isAuthenticated && <UserBadge />}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-4">
              {isAuthenticated && <UserBadge />}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-white hover:text-blue-400 transition-colors"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Mobile Menu */}
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-slate-900 z-50 transform transition-transform duration-300 ease-in-out md:hidden">
            <div className="flex flex-col h-full">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-6 border-b border-blue-800/30">
                <div className="text-xl font-bold text-white">
                  Strat<span className="text-blue-400">IQ</span><span className="text-cyan-400">X</span>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Close mobile menu"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Mobile Navigation Items */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-6">
                  {/* Main Navigation */}
                  <div className="space-y-4">
                    {navigationItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleNavigate(item.path)}
                        className={cn(
                          "block w-full text-left px-4 py-3 rounded-lg transition-colors",
                          {
                            'bg-blue-600/20 text-blue-400': isActivePage(item.path),
                            'text-gray-300 hover:text-white hover:bg-slate-800': !isActivePage(item.path)
                          }
                        )}
                      >
                        <div className="font-medium">{item.label}</div>
                        {item.description && (
                          <div className="text-sm text-gray-400 mt-1">{item.description}</div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* CTA Section */}
                  {!isAuthenticated && (
                    <div className="pt-4 border-t border-blue-800/30">
                      <Button 
                        onClick={handleGetStarted}
                        className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white py-3"
                      >
                        {NAVIGATION_ACTIONS.primary.label}
                      </Button>
                      <p className="text-xs text-gray-400 text-center mt-2">
                        {NAVIGATION_ACTIONS.primary.description}
                      </p>
                    </div>
                  )}

                  {/* Footer Links in Mobile */}
                  <div className="pt-4 border-t border-blue-800/30">
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">
                      Legal
                    </div>
                    <div className="space-y-2">
                      {NavigationConfig.getFooterNavigation().map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleNavigate(item.path)}
                          className="block text-sm text-gray-400 hover:text-gray-300 transition-colors"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Menu Footer */}
              <div className="p-6 border-t border-blue-800/30">
                <p className="text-xs text-gray-400 text-center">
                  Strategic Intelligence Amplified
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default MainNavigation